import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import {
  ClientResponse,
  EmployeeResponse,
  EventResponse,
  PlanningEntityIdentifier,
  PlanningResponse,
  RemoveAssignmentFromAppointmentsRequest
} from 'generated/index';
import { uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { View, ViewContext } from '../enums';

import {
  appendCorrelationToken,
  getClientName,
  getSelectableStatus,
  getViewContext
} from '../utils';
import { formatByLocale, formatHoursMinutes } from 'utils/date';

import { mutations, queries } from 'RestAPIs/rapini';

import { Box, Checkbox, Chip, TextField, Typography } from '@mui/material';

import { LoadingOverlay, VirtualizedAutocomplete } from 'components/shared';
import CustomNoRowsOverlay from 'components/shared/DataGrid/NoRowsOverlay';
import MenuButton from 'components/shared/MenuButton';
import showSnackbar, {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';

import BottomHeader from '../components/BottomHeader/BottomHeader';
import ListViewTable from './components/ListViewTable';

import usePlanningEntity, {
  PlanningEntityTypePreference
} from '../hooks/usePlanningEntity';
import useSelectedDate from '../hooks/useSelectedDate';
import useGetColumnsByEntityType from './hooks/useGetColumnsByEntity';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useSessionStorage } from 'usehooks-ts';
import { LISTVIEW_SESSION_STORAGE_KEYS } from './types';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  planUnitsAtom,
  selectedPlanningEntitiesAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';
import { ListViewContext } from './ListViewContext';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { useNavParams } from 'hooks';

export const getGridTemplateColumns = (columns: GridColDef[]) =>
  '40px ' +
  columns.map((c) => (c.width ? c.width + 'px' : '1fr')).join(' ') +
  ' 40px'; // +1 for the menu column

type EntitiesFilter = {
  id: string | undefined;
  name: string | undefined;
  entityId?: number;
  number?: number;
}[];

interface ListViewProps {
  data: PlanningResponse | undefined;
  isLoading: boolean;
  employees: EmployeeResponse[];
}

const ListView = ({
  data,
  isLoading: _isLoading,
  employees
}: ListViewProps) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { params } = useNavParams();
  const view = params?.view as View;

  const [selectedDate] = useSelectedDate();
  const startDate = dayjs(selectedDate).startOf('isoWeek').format('YYYY-MM-DD');
  const [entity, setEntity] = usePlanningEntity();
  const selectedEntity = entity[view];

  const [entitiesFilter, setEntitiesFilter] = useState<EntitiesFilter>([]);
  const [, setSelectedPlanningEntityIdentifiers] = useRecoilState(
    selectedPlanningEntitiesAtom
  );
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [checked, setChecked] = useState<string[]>([]);
  const { data: clients } = queries.useGetClients(
    // eslint-disable-next-line
    // @ts-ignore
    selectedPlanUnits?.length > 0
      ? selectedPlanUnits.map((planUnit) => planUnit.id).join(',')
      : undefined,
    {
      enabled: selectedEntity === 'CLIENT'
    }
  );
  const [filteredClientIds, setFilteredClientIds] = useSessionStorage<string[]>(
    LISTVIEW_SESSION_STORAGE_KEYS.FILTERED_CLIENTS,
    []
  );
  const isInitialMount = useRef<boolean>(true);

  useEffect(() => {
    setSelectedPlanningEntityIdentifiers(
      entitiesFilter
        .filter(({ id }) => !!id)
        .map(
          ({ id, entityId, number }) =>
            ({
              planningEntityType: selectedEntity,
              externalEntityId: entityId || id,
              number
            }) as PlanningEntityIdentifier
        )
    );
  }, [entitiesFilter]);

  useEffect(() => {
    if (filteredClientIds.length > 0 && isInitialMount.current) {
      setEntity({ [view]: 'CLIENT' } as PlanningEntityTypePreference);
    }

    if (clients && clients.length > 0 && filteredClientIds.length > 0) {
      setEntitiesFilter(
        clients
          .filter(({ id }) => filteredClientIds.includes(id))
          .map((client: ClientResponse) => ({
            id: client.id,
            name: `${client.number} - ${getClientName(client)}`,
            number: client.number
          }))
      );
      isInitialMount.current = false;
    }
  }, [filteredClientIds, clients]);

  useEffect(() => {
    setEntitiesFilter([]);
    setChecked([]);
  }, [selectedEntity]);

  useEffect(() => {
    const set = () => setFilteredClientIds([]);

    // Clean filtered clients from session upon hard reload
    window.addEventListener('beforeunload', set);
    // Clean filtered clients from session when ListView unmounts and clear listeners
    return () => {
      setFilteredClientIds([]);
      window.removeEventListener('beforeunload', set);
    };
  }, []);

  const eventsByDay = useMemo(() => {
    const eventsByDay = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    };

    if (data?.planningEntities) {
      const allEvents = [
        ...data.planningEntities.flatMap((item) => item.events),
        ...data.planningEntities.flatMap((item) =>
          item.schedules.flatMap((schedule) =>
            schedule.events.filter(
              (event) =>
                event.category !== 'TRAVELTIME' && event.category !== 'FREE'
            )
          )
        )
      ];
      // Sort the events by time
      const startOfWeek = dayjs(selectedDate).startOf('isoWeek');
      const startOfNextWeek = startOfWeek.add(1, 'week');
      allEvents
        .filter(
          (event) =>
            event &&
            !dayjs(event.start).isBefore(startOfWeek) &&
            dayjs(event.start).isBefore(startOfNextWeek)
        )
        .sort((a, b) => {
          return dayjs(a!.start).diff(dayjs(b!.start));
        })
        .forEach((event) => {
          if (event) {
            const day = dayjs(event.date)
              .locale('en')
              .format('dddd')
              .toLowerCase();
            eventsByDay[day].push(event);
          }
        });
    }

    return eventsByDay;
  }, [data]);

  const options = useMemo(() => {
    if (selectedEntity === 'CLIENT') {
      return (
        clients?.map((client) => ({
          id: client.id,
          name: `${client.number} - ${getClientName(client)}`,
          number: client.number
        })) ?? []
      );
    }

    if (selectedEntity === 'EMPLOYEE') {
      return employees.map((employee) => ({
        id: employee.id,
        entityId: employee.entityId,
        name: employee.nameInformal,
        number: employee.number
      }));
    }

    if (selectedEntity === 'WORKPLACE' || selectedEntity === 'PROJECT') {
      return (
        data?.planningEntities
          .filter((item) => item.type === selectedEntity)
          .map((item) => ({
            id: item.id,
            name: item.name
          })) ?? []
      );
    }

    return [];
  }, [data, clients, employees, entity]);

  const renderTags = (options) => {
    if (options.length > 1) {
      return options.map((option) => {
        return (
          <Chip
            key={option.id}
            label={option.name}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            onDelete={() =>
              setEntitiesFilter(
                entitiesFilter.filter((item) => item.id !== option.id)
              )
            }
          />
        );
      });
    } else {
      return <div>{options[0].name}</div>;
    }
  };

  function handleCheckboxChange(e, value: string[] | string) {
    if (e.target.checked) {
      if (typeof value === 'string') {
        setChecked((prev) => [...prev, value]);
        return;
      }
      setChecked((prev) => uniq([...prev, ...value]));
    } else {
      if (typeof value === 'string') {
        setChecked((prev) => prev.filter((id) => id !== value));
        return;
      }

      setChecked((prev) => prev.filter((id) => !value.includes(id)));
    }
  }

  const { mutate: approveAppointments } = mutations.useApproveAppointments();
  const { mutate: submitAppointments } = mutations.useSubmitAppointments();
  const {
    mutateAsync: unassignAppointmentFromAppointment,
    isLoading: isLoadingUnassignAppointment
  } = mutations.useRemoveAssignmentFromAppointments();

  const context = getViewContext();
  function onButtonClick(appointmentEntityIds: string[]) {
    if (context == ViewContext.Own) {
      submitAppointments(
        {
          appointmentEntityIds
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('planning');
            setChecked([]);
            showSuccessSnackbar();
          },
          onError: () => {
            showErrorSnackbar();
          }
        }
      );
      return;
    }
    approveAppointments(
      {
        appointmentEntityIds
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('planning');
          setChecked([]);
          showSuccessSnackbar();
        },
        onError: () => {
          showErrorSnackbar();
        }
      }
    );
  }

  const allEvents: EventResponse[] = Object.values(eventsByDay).flat();
  const hasEvents = allEvents.length > 0;

  const allSelectableEvents = allEvents.filter(getSelectableStatus);
  const isSelectable = allSelectableEvents.length > 0;

  const columns = useGetColumnsByEntityType(selectedEntity, allEvents);

  function getFilteredEvents(events: EventResponse[]) {
    return events.filter(({ workPlace, project, clients, employees }) => {
      if (entitiesFilter.length === 0) return true;

      if (selectedEntity === 'CLIENT') {
        return clients.some(({ id }) =>
          entitiesFilter.some(({ id: entityId }) => entityId === id)
        );
      }
      if (selectedEntity === 'EMPLOYEE') {
        return employees.some(({ id }) =>
          entitiesFilter.some(({ id: entityId }) => entityId === id)
        );
      }

      return entitiesFilter.some(({ id: entityId }) => {
        return (
          entityId === workPlace?.entityId || entityId === project?.entityId
        );
      });
    });
  }

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    const unassignAppointmentId: string = shouldShowPopoverDialog?.id || '';
    const event = allEvents.find(
      (event: EventResponse) => event.id === unassignAppointmentId
    );

    if (!event) {
      return showErrorSnackbar(t('activityPlanning.unassignAppointmentError'));
    }

    const payload: RemoveAssignmentFromAppointmentsRequest = {
      appointmentOccurrences: [
        {
          entityId: event?.id,
          originalOccurrenceStart: event?.start,
          schedulingLabel: event?.schedulingLabel
        }
      ],
      editAppointmentType
    };

    const unassignPayload = appendCorrelationToken
      ? appendCorrelationToken(payload)
      : payload;

    unassignAppointmentFromAppointment(unassignPayload)
      .catch(() =>
        showErrorSnackbar(t('activityPlanning.unassignAppointmentError'))
      )
      .then(() =>
        showSnackbar({
          message: t('activityPlanning.unassignAppointmentSuccess'),
          severity: 'success'
        })
      )
      .finally(() => queryClient.invalidateQueries('planning'));
  }

  function onHandleDisconnectAppointmentConfirmation(
    editAppointmentType: EditAppointmentType
  ) {
    unassignAppointment(editAppointmentType);

    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  }

  function onReset() {
    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  }

  const totalDuration = getFilteredEvents(allEvents).reduce(
    (acc, event) => acc + event.durationInSeconds,
    0
  );

  const isLoading = _isLoading || isLoadingUnassignAppointment;

  return (
    <ListViewContext.Provider
      value={{
        onHandleDisconnectAppointmentConfirmation,
        onClose: onReset,
        isLoading
      }}
    >
      <Box display="flex" flexDirection="column" height="calc(100% - 80px)">
        <BottomHeader sx={{ justifyContent: 'space-between' }}>
          <Box
            display="flex"
            alignItems="center"
            minWidth="250px"
            height="54px"
          >
            <VirtualizedAutocomplete
              autoComplete
              multiple
              fullWidth
              options={options as EntitiesFilter}
              limitTags={entitiesFilter.length > 1 ? 0 : 1}
              value={entitiesFilter}
              renderTags={renderTags}
              getLimitTagsText={(cnt) => {
                return `${cnt} ${t('selected').toLocaleLowerCase()}`;
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(event, value, reason) => {
                if (reason === 'clear') {
                  setEntitiesFilter([]);
                  setFilteredClientIds([]);
                }
              }}
              onChange={(e, value) => {
                setEntitiesFilter(value as EntitiesFilter);
                setFilteredClientIds([]); // Reset the locally stored filter if we use the autocomplete
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return option.name as string;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Selecteer ${t(
                    `activityPlanning.planningEntityType.${selectedEntity}`
                  ).toLowerCase()}`}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            {checked.length > 0 && (
              <Typography>
                {checked.length} {t('selected').toLocaleLowerCase()}
              </Typography>
            )}
            <MenuButton
              label="Acties"
              disabled={checked.length === 0}
              options={[
                {
                  label:
                    context == ViewContext.Own
                      ? t('activityPlanning.submitHours')
                      : t('activityPlanning.approveHours'),
                  onClick: () => onButtonClick(checked)
                }
              ]}
            />
          </Box>
        </BottomHeader>
        {!isLoading && !hasEvents && <CustomNoRowsOverlay />}
        <Box
          sx={{
            flex: '1 1 100%',
            overflow: 'hidden',
            overflowY: 'auto'
          }}
        >
          {hasEvents && (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 50,
                background: 'white',
                padding: '16px'
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: getGridTemplateColumns(columns),
                  alignItems: 'center'
                }}
              >
                {columns.map((c, i) => {
                  return (
                    <Fragment key={`${i}-${columns[i].field}`}>
                      {i === 0 &&
                        (isSelectable ? (
                          <Checkbox
                            indeterminate={
                              checked.length < allSelectableEvents.length &&
                              checked.length > 0
                            }
                            checked={
                              isSelectable &&
                              allSelectableEvents.length === checked.length
                            }
                            onChange={(e) => {
                              const allIds = allSelectableEvents.map(
                                ({ id }) => id
                              );
                              handleCheckboxChange(e, allIds);
                            }}
                          />
                        ) : (
                          <Box width={40} />
                        ))}
                      <Box
                        sx={{
                          fontFamily: 'signikasemibold',
                          fontSize: '14px'
                        }}
                        width={c.width}
                      >
                        {c.headerName}
                      </Box>
                    </Fragment>
                  );
                })}
              </Box>
            </Box>
          )}
          <Box px={2}>
            {Object.entries(eventsByDay).map(
              ([day, events]: [string, EventResponse[]], i) => {
                const filteredEvents = getFilteredEvents(events);
                if (filteredEvents.length === 0) {
                  return null;
                }
                return (
                  <ListViewTable
                    key={day}
                    title={`${t(`weekday.${day}`)} ${formatByLocale(
                      dayjs(startDate).add(i, 'day').toDate(),
                      'dd MMMM'
                    )}`}
                    columns={columns}
                    events={filteredEvents}
                    selectableEvents={events.filter(getSelectableStatus)}
                    onCheckboxChange={handleCheckboxChange}
                    checked={checked}
                  />
                );
              }
            )}
          </Box>
          {allEvents?.length > 0 && (
            <Box
              sx={{
                paddingLeft: '58px',
                fontFamily: 'signikasemibold',
                fontSize: '15px'
              }}
            >
              {`${t('total')}: ` + formatHoursMinutes(totalDuration)}
            </Box>
          )}
          <LoadingOverlay loading={isLoading} />
        </Box>
      </Box>
    </ListViewContext.Provider>
  );
};

export default ListView;
